import { Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core'
import { NgClass } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faMicrophone, faWandMagicSparkles, faCheck, faPenToSquare, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons'

import {
    DisciplinaryAction,
    DisciplinaryActionExtractionResult,
    EmployeeMatchResult,
    RepositoryModal,
} from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { defaultVoiceAutoStartForPlatform } from '../../shared/components/voice-text-field/default-voice-auto-start'
import { DisciplinaryActionListComponent } from './disciplinary-action-list.component'

@Component({
    templateUrl: './disciplinary-action-modal.component.html',
    styleUrls: ['./disciplinary-action-modal.component.css'],
    standalone: true,
    imports: [FormsModule, FaIconComponent, NgClass, VoiceTextFieldComponent],
    encapsulation: ViewEncapsulation.None,
})
export class DisciplinaryActionModalComponent extends RepositoryModal<DisciplinaryAction> implements OnInit {
    private destroyRef = inject(DestroyRef)

    parentComponent!: DisciplinaryActionListComponent

    micIcon = faMicrophone
    wandIcon = faWandMagicSparkles
    checkIcon = faCheck
    editIcon = faPenToSquare
    arrowRightIcon = faArrowRight
    arrowLeftIcon = faArrowLeft

    currentStep = 1

    readonly voiceAutoStart = defaultVoiceAutoStartForPlatform()
    finalText = ''

    // Extraction state (Step 2 sub-state)
    isExtracting = false
    extractionError = ''
    extractionResult: DisciplinaryActionExtractionResult | null = null

    // Employee match display (Step 3)
    employeeMatchMessage = ''
    topMatchConfidence = ''

    // Date binding helpers
    dateString = ''
    followUpDateString = ''

    get jobTitle(): string {
        const emp = this.parentComponent?.employees?.find(e => e.hrRef === this.target.hrRef)
        return emp?.jobTitle ?? ''
    }

    ngOnInit() {
        this.parentComponent = this.editOptions.initializeParams.component as DisciplinaryActionListComponent

        if (this.isAdding) {
            this.target.hrco = 1
            this.target.hrRef = 0
            this.target.status = 'Draft'
            this.currentStep = 1
        } else {
            this.currentStep = 3
            this.syncDatesToStrings()
        }
    }

    goToStep(step: number) {
        this.currentStep = step
    }

    // --- Step 3: AI Extraction ---

    sendToAi() {
        this.isExtracting = true
        this.extractionError = ''

        this.services.data
            .post<DisciplinaryActionExtractionResult>(
                'api/DisciplinaryAction/ExtractFromTranscription',
                { transcription: this.finalText },
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.extractionResult = result
                    this.applyExtractionToTarget(result)
                    this.isExtracting = false
                    this.currentStep = 3
                },
                error: err => {
                    this.extractionError = err?.error?.error || 'An unexpected error occurred.'
                    this.isExtracting = false
                },
            })
    }

    private applyExtractionToTarget(result: DisciplinaryActionExtractionResult) {
        this.target.rawTranscription = this.finalText
        this.target.employeeName = result.employeeName || ''

        if (result.incidentDate) {
            const parsed = this.parseToDateInputString(result.incidentDate)
            this.dateString = parsed.dateString
            this.target.date = parsed.date
        } else {
            const today = new Date()
            this.dateString = this.formatAsDateInput(today)
            this.target.date = today
        }
        if (result.followUpDate) {
            const parsed = this.parseToDateInputString(result.followUpDate)
            this.followUpDateString = parsed.dateString
            this.target.followUpDate = parsed.date
        }

        this.target.code = result.code || ''
        this.target.incidentDesc = result.incidentDesc || ''
        this.target.discAction = result.discAction || ''
        this.target.daysSuspended = result.daysSuspended ?? undefined
        this.target.warningNoticeYn = result.warningNoticeYn || ''
        this.target.udProgressiveDiscipline = result.udProgressiveDiscipline || ''
        this.target.udEmployeeComments = result.udEmployeeComments || ''
        this.target.udSupervisorComments = result.udSupervisorComments || ''

        this.applyEmployeeMatches(result.employeeMatches)
    }

    // --- Step 4: Employee matching ---

    private applyEmployeeMatches(matches: EmployeeMatchResult[]) {
        if (!matches || matches.length === 0) {
            this.employeeMatchMessage = 'No employee match found. Please select manually.'
            this.topMatchConfidence = ''
            return
        }

        const top = matches[0]
        this.topMatchConfidence = top.confidence

        if (top.confidence === 'high') {
            this.target.hrRef = top.hrRef
            this.target.employeeName = `${top.firstName} ${top.lastName}`
            this.employeeMatchMessage = `AI matched: ${top.firstName} ${top.lastName} (${top.confidence} confidence)`
        } else {
            this.target.hrRef = top.hrRef
            this.employeeMatchMessage = `AI suggestion: ${top.firstName} ${top.lastName} (${top.confidence} confidence) — please verify`
        }
    }

    onEmployeeSelected() {
        this.employeeMatchMessage = ''
        const emp = this.parentComponent.employees.find(e => e.hrRef === this.target.hrRef)
        if (emp) {
            this.target.employeeName = `${emp.firstName} ${emp.lastName}`
        }
    }

    // --- Date helpers ---

    private syncDatesToStrings() {
        if (this.target.date) {
            this.dateString = this.toDateInputString(this.target.date)
        }
        if (this.target.followUpDate) {
            this.followUpDateString = this.toDateInputString(this.target.followUpDate)
        }
    }

    private toDateInputString(d: Date): string {
        if (!d) return ''
        const dt = new Date(d)
        return this.formatAsDateInput(dt)
    }

    private parseToDateInputString(value: string | null | undefined): { dateString: string; date: Date | undefined } {
        if (!value) return { dateString: '', date: undefined }
        const str = String(value)
        const parts = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (parts) {
            const dt = new Date(+parts[1], +parts[2] - 1, +parts[3])
            return { dateString: `${parts[1]}-${parts[2]}-${parts[3]}`, date: dt }
        }
        const dt = new Date(str)
        if (isNaN(dt.getTime())) return { dateString: '', date: undefined }
        return { dateString: this.formatAsDateInput(dt), date: dt }
    }

    private formatAsDateInput(dt: Date): string {
        const year = dt.getFullYear()
        const month = String(dt.getMonth() + 1).padStart(2, '0')
        const day = String(dt.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    protected onBeforeSave(): boolean {
        if (this.dateString) {
            this.target.date = new Date(this.dateString)
        }
        if (this.followUpDateString) {
            this.target.followUpDate = new Date(this.followUpDateString)
        }
        if (!this.target.createdOn) {
            this.target.createdOn = new Date()
        }
        return true
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: successResult => {
                resolveSave(successResult)
            },
            error: errorResult => {
                rejectSave(errorResult)
            },
        })
    }
}
